import { useState, useEffect, useRef, useCallback } from 'react';
import { getRelatedVideos, CommunityVideoEntry } from '../videoData';

export interface VideoPreviewData {
  id: string;
  thumbnail: string;
  prompt: string;
  style?: string;
  duration?: string;
  author?: string;
  avatar?: string;
  views?: string;
  videoType?: string;
}

interface VideoPreviewModalProps {
  video: VideoPreviewData;
  darkMode: boolean;
  onClose: () => void;
  onRemix?: (prompt: string) => void;
}

const DURATION_SECONDS: Record<string, number> = {
  '15s': 15,
  '30s': 30,
  '60s': 60,
  '2min': 120,
  '5min': 300,
};

const STYLE_COLORS: Record<string, string> = {
  Cinematic: 'bg-orange-100 text-orange-700',
  Realistic: 'bg-emerald-100 text-emerald-700',
  Animated: 'bg-rose-100 text-rose-700',
  Abstract: 'bg-amber-100 text-amber-700',
};

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function VideoPreviewModal({ video, darkMode, onClose, onRemix }: VideoPreviewModalProps) {
  // ── internal navigation state ──
  const [currentVideo, setCurrentVideo] = useState<VideoPreviewData>(video);

  // keep in sync if external video prop changes
  useEffect(() => { setCurrentVideo(video); }, [video]);

  const relatedVideos: CommunityVideoEntry[] = getRelatedVideos(currentVideo, 4);

  const navigateTo = (next: VideoPreviewData) => {
    setCurrentVideo(next);
    setCurrentTime(0);
    setIsPlaying(true);
  };

  const totalSeconds = DURATION_SECONDS[currentVideo.duration ?? '30s'] ?? 30;

  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(70);
  const [isMuted, setIsMuted] = useState(false);
  const [isDraggingSeek, setIsDraggingSeek] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [particles, setParticles] = useState<{ id: number; x: number; y: number; size: number; opacity: number; speed: number }[]>([]);

  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekBarRef = useRef<HTMLDivElement>(null);

  // Spawn cinematic particles
  useEffect(() => {
    const initial = Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 1 + Math.random() * 2,
      opacity: 0.2 + Math.random() * 0.5,
      speed: 0.03 + Math.random() * 0.06,
    }));
    setParticles(initial);
  }, []);

  // Drift particles upward while playing
  useEffect(() => {
    if (!isPlaying) return;
    const drift = setInterval(() => {
      setParticles(prev => prev.map(p => ({
        ...p,
        y: p.y - p.speed > -2 ? p.y - p.speed : 102,
        x: p.x + (Math.random() - 0.5) * 0.2,
      })));
    }, 50);
    return () => clearInterval(drift);
  }, [isPlaying]);

  // Playback tick
  useEffect(() => {
    if (isPlaying && !isDraggingSeek) {
      tickRef.current = setInterval(() => {
        setCurrentTime(t => {
          if (t >= totalSeconds) { return 0; } // loop
          return t + 0.1;
        });
      }, 100);
    } else {
      if (tickRef.current) clearInterval(tickRef.current);
    }
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [isPlaying, isDraggingSeek, totalSeconds]);

  // Auto-hide controls
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    resetControlsTimer();
    return () => { if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current); };
  }, [resetControlsTimer]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === ' ' || e.key === 'k') { e.preventDefault(); setIsPlaying(p => !p); }
      if (e.key === 'm') setIsMuted(p => !p);
      if (e.key === 'ArrowRight') setCurrentTime(t => Math.min(t + 5, totalSeconds));
      if (e.key === 'ArrowLeft') setCurrentTime(t => Math.max(t - 5, 0));
      if (e.key === 'f') setIsFullscreen(p => !p);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, totalSeconds]);

  // Seek bar click / drag
  const handleSeekClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!seekBarRef.current) return;
    const rect = seekBarRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setCurrentTime(ratio * totalSeconds);
  };

  const progressPct = (currentTime / totalSeconds) * 100;
  const effectiveVolume = isMuted ? 0 : volume;

  // Scan-line animation phase for cinematic effect
  const scanY = ((currentTime * 4) % 110) - 10;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />

      {/* Modal */}
      <div
        className={`relative z-10 flex flex-col transition-all duration-300 ${
          isFullscreen
            ? 'w-full h-full rounded-none'
            : 'w-full max-w-4xl mx-4 rounded-2xl overflow-hidden'
        }`}
        onClick={e => e.stopPropagation()}
        onMouseMove={resetControlsTimer}
      >
        {/* ── VIDEO AREA ── */}
        <div
          className="relative bg-black select-none"
          style={{ aspectRatio: isFullscreen ? undefined : '16/9', flex: isFullscreen ? 1 : undefined }}
          onClick={() => { setIsPlaying(p => !p); resetControlsTimer(); }}
        >
          {/* Poster frame */}
          <img
            src={currentVideo.thumbnail}
            alt={currentVideo.prompt}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />

          {/* Film-grain overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
              opacity: 0.35,
              mixBlendMode: 'overlay',
            }}
          />

          {/* Scan-line sweep (cinematic effect) */}
          {isPlaying && (
            <div
              className="absolute left-0 right-0 h-1 pointer-events-none"
              style={{
                top: `${scanY}%`,
                background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.04), transparent)',
              }}
            />
          )}

          {/* Floating particles */}
          {particles.map(p => (
            <div
              key={p.id}
              className="absolute rounded-full bg-white pointer-events-none"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                opacity: isPlaying ? p.opacity : 0,
                transition: 'opacity 0.5s',
              }}
            />
          ))}

          {/* Vignette */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.55) 100%)' }}
          />

          {/* Big centre play/pause icon (flash on toggle) */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-20 h-20 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center animate-[fadeOut_0.8s_ease_forwards]">
                <i className="ri-pause-fill text-white text-4xl"></i>
              </div>
            </div>
          )}

          {/* ── CONTROLS OVERLAY ── */}
          <div
            className={`absolute inset-0 flex flex-col justify-between transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
            style={{ pointerEvents: showControls ? 'auto' : 'none' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Top bar */}
            <div className="flex items-start justify-between p-4" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.65), transparent)' }}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {currentVideo.style && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${STYLE_COLORS[currentVideo.style] ?? 'bg-gray-100 text-gray-700'}`}>
                    {currentVideo.style}
                  </span>
                )}
                {currentVideo.duration && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/50 text-white flex-shrink-0">
                    {currentVideo.duration}
                  </span>
                )}
                <span className="text-white/80 text-xs truncate hidden sm:block">{currentVideo.prompt.slice(0, 80)}{currentVideo.prompt.length > 80 ? '…' : ''}</span>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors flex-shrink-0 ml-3 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Bottom controls bar */}
            <div style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.80), transparent)', padding: '0 16px 16px' }}>
              {/* Seek bar */}
              <div
                ref={seekBarRef}
                className="relative h-5 flex items-center cursor-pointer mb-3 group"
                onClick={handleSeekClick}
                onMouseDown={() => setIsDraggingSeek(true)}
                onMouseUp={() => setIsDraggingSeek(false)}
              >
                {/* Track */}
                <div className="w-full h-1 group-hover:h-1.5 transition-all rounded-full bg-white/25 overflow-visible">
                  {/* Buffer bar */}
                  <div className="h-full rounded-full bg-white/20" style={{ width: `${Math.min(progressPct + 18, 100)}%` }} />
                  {/* Progress fill */}
                  <div
                    className="absolute top-1/2 left-0 h-1 group-hover:h-1.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-red-500 to-orange-400 transition-all"
                    style={{ width: `${progressPct}%` }}
                  >
                    {/* Thumb */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </div>

              {/* Controls row */}
              <div className="flex items-center gap-3">
                {/* Play / Pause */}
                <button
                  onClick={() => setIsPlaying(p => !p)}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/15 text-white transition-all cursor-pointer flex-shrink-0"
                >
                  <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-xl`}></i>
                </button>

                {/* Skip back/forward */}
                <button
                  onClick={() => setCurrentTime(t => Math.max(0, t - 10))}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/15 text-white transition-all cursor-pointer hidden sm:flex"
                  title="Back 10s"
                >
                  <i className="ri-replay-10-line text-lg"></i>
                </button>
                <button
                  onClick={() => setCurrentTime(t => Math.min(totalSeconds, t + 10))}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/15 text-white transition-all cursor-pointer hidden sm:flex"
                  title="Forward 10s"
                >
                  <i className="ri-forward-10-line text-lg"></i>
                </button>

                {/* Loop badge */}
                <span className="text-white/50 text-[10px] font-semibold flex items-center gap-1 hidden sm:flex">
                  <i className="ri-loop-right-line text-xs"></i>Loop
                </span>

                {/* Time */}
                <span className="text-white/80 text-xs font-mono flex-shrink-0">
                  {formatTime(currentTime)} / {formatTime(totalSeconds)}
                </span>

                <div className="flex-1" />

                {/* Volume control */}
                <div
                  className="relative flex items-center gap-1.5"
                  onMouseEnter={() => setShowVolumeSlider(true)}
                  onMouseLeave={() => setShowVolumeSlider(false)}
                >
                  <button
                    onClick={() => setIsMuted(p => !p)}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/15 text-white transition-all cursor-pointer"
                  >
                    <i className={`${effectiveVolume === 0 ? 'ri-volume-mute-fill' : effectiveVolume < 40 ? 'ri-volume-down-fill' : 'ri-volume-up-fill'} text-lg`}></i>
                  </button>

                  {/* Volume slider appears on hover */}
                  <div className={`flex items-center transition-all duration-200 overflow-hidden ${showVolumeSlider ? 'w-20 opacity-100' : 'w-0 opacity-0'}`}>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={volume}
                      onChange={e => { setVolume(Number(e.target.value)); setIsMuted(false); }}
                      className="w-full h-1 accent-red-500 cursor-pointer"
                    />
                  </div>
                  <span className={`text-white/60 text-[10px] font-mono transition-all duration-200 ${showVolumeSlider ? 'w-6 opacity-100' : 'w-0 opacity-0 overflow-hidden'}`}>
                    {effectiveVolume}
                  </span>
                </div>

                {/* Fullscreen */}
                <button
                  onClick={() => setIsFullscreen(p => !p)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/15 text-white transition-all cursor-pointer"
                  title={isFullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}
                >
                  <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-lg`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── INFO PANEL (below video, hidden in fullscreen) ── */}
        {!isFullscreen && (
          <div className={`px-5 py-4 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className="flex items-start gap-3">
              {/* Author */}
              {currentVideo.avatar && (
                <img src={currentVideo.avatar} alt={currentVideo.author} className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {currentVideo.author && (
                    <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{currentVideo.author}</span>
                  )}
                  {currentVideo.views && (
                    <span className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                      <i className="ri-eye-line"></i>{currentVideo.views} views
                    </span>
                  )}
                </div>
                <p className={`text-xs leading-relaxed line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{currentVideo.prompt}</p>
              </div>

              {/* Keyboard shortcut hints */}
              <div className={`hidden lg:flex flex-col gap-1 text-[10px] flex-shrink-0 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                <span><kbd className={`px-1 py-0.5 rounded text-[9px] font-mono ${darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>Space</kbd> Play/Pause</span>
                <span><kbd className={`px-1 py-0.5 rounded text-[9px] font-mono ${darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>← →</kbd> Seek 5s</span>
                <span><kbd className={`px-1 py-0.5 rounded text-[9px] font-mono ${darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>M</kbd> Mute &nbsp;<kbd className={`px-1 py-0.5 rounded text-[9px] font-mono ${darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>F</kbd> Fullscreen</span>
              </div>
            </div>

            {/* Remix button (community videos only) */}
            {onRemix && (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => { onRemix(currentVideo.prompt); onClose(); }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 text-white font-semibold text-sm rounded-xl hover:shadow-lg hover:shadow-red-500/25 transition-all"
                >
                  <i className="ri-loop-right-line"></i>
                  Remix this Prompt
                </button>
                <button
                  onClick={onClose}
                  className={`flex items-center gap-2 px-5 py-2.5 font-semibold text-sm rounded-xl transition-all ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Close
                </button>
              </div>
            )}

            {/* ── More Like This ── */}
            {relatedVideos.length > 0 && (
              <div className={`mt-5 pt-4 border-t ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 flex items-center justify-center ${darkMode ? 'text-orange-400' : 'text-orange-500'}`}>
                      <i className="ri-fire-line text-sm"></i>
                    </span>
                    <h4 className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>More Like This</h4>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                      {currentVideo.style ?? 'Similar style'}
                    </span>
                  </div>
                  <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Click to switch</span>
                </div>

                <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                  {relatedVideos.map(v => {
                    const isActive = v.id === currentVideo.id;
                    return (
                      <button
                        key={v.id}
                        onClick={() => navigateTo(v)}
                        className={`flex-shrink-0 rounded-xl overflow-hidden transition-all duration-200 group ${
                          isActive
                            ? 'ring-2 ring-red-500 scale-[1.03]'
                            : 'hover:scale-[1.02]'
                        }`}
                        style={{ width: '140px' }}
                      >
                        {/* Thumb */}
                        <div className="relative" style={{ height: '79px' }}>
                          <img
                            src={v.thumbnail}
                            alt={v.prompt}
                            className="w-full h-full object-cover object-top"
                          />
                          {/* Hover / active overlay */}
                          <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${isActive ? 'bg-red-500/30' : 'bg-black/0 group-hover:bg-black/40'}`}>
                            {!isActive && (
                              <div className="w-8 h-8 rounded-full bg-white/85 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <i className="ri-play-fill text-red-500 text-base ml-0.5"></i>
                              </div>
                            )}
                            {isActive && (
                              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                                <i className="ri-play-fill text-white text-base ml-0.5"></i>
                              </div>
                            )}
                          </div>
                          {/* Style badge */}
                          {v.style && (
                            <div className="absolute top-1.5 left-1.5">
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${STYLE_COLORS[v.style] ?? 'bg-gray-100 text-gray-700'}`}>
                                {v.style}
                              </span>
                            </div>
                          )}
                          {/* Duration badge */}
                          {v.duration && (
                            <div className="absolute top-1.5 right-1.5">
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-black/60 text-white">
                                {v.duration}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Meta */}
                        <div className={`px-2 py-1.5 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          {v.author && (
                            <p className={`text-[10px] font-semibold truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{v.author}</p>
                          )}
                          <p className={`text-[9px] truncate mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {v.prompt.slice(0, 38)}{v.prompt.length > 38 ? '…' : ''}
                          </p>
                          {v.views && (
                            <p className={`text-[9px] mt-0.5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                              <i className="ri-eye-line mr-0.5"></i>{v.views}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeOut {
          0% { opacity: 1; transform: scale(1); }
          60% { opacity: 1; transform: scale(1.1); }
          100% { opacity: 0; transform: scale(0.95); }
        }
      `}</style>
    </div>
  );
}
